import { getElementId } from './format';
/**
 * Date-limit helpers for the form constructor's "date" element.
 *
 * The form-constructor date element supports two ways of expressing
 * a min/max boundary:
 *
 *   1. FIXED   — an absolute ISO date (e.g. '2026-12-31'). The same
 *                date applies every time the form is opened.
 *   2. RELATIVE — today + N days/months/years. Resolved at the moment
 *                the citizen opens the form. Lets the admin write
 *                "earliest = today" (= relative 0 days) or "latest =
 *                today + 6 months" once and have it stay sensible
 *                indefinitely.
 *
 * Stored shape on a date element:
 *
 *   min_date / max_date:
 *     | null                                         // no limit
 *     | { mode: 'fixed',    value: '2026-06-05' }
 *     | { mode: 'relative', amount: 6, unit: 'months' }
 *
 *   unit ∈ 'days' | 'months' | 'years'
 *
 * Older saved forms used a bare ISO string ('' meant "no limit").
 * normalizeDateLimit() handles that legacy shape transparently, so
 * no DB migration is required.
 *
 * 0.35.94 — Introduced.
 */

/** Coerce any legacy or new-shape value into the canonical form. */
export function normalizeDateLimit(raw) {
  if (raw == null || raw === '') return null;
  // Legacy string shape.
  if (typeof raw === 'string') {
    // Anything non-empty here is interpreted as a fixed ISO date.
    // We don't validate the format strictly — bad strings just
    // resolve to "no limit" in resolveDateLimit() below.
    return { mode: 'fixed', value: raw };
  }
  if (typeof raw === 'object') {
    if (raw.mode === 'fixed') {
      return { mode: 'fixed', value: raw.value || '' };
    }
    if (raw.mode === 'relative') {
      const amount = Number.isFinite(raw.amount) ? Math.trunc(raw.amount) : 0;
      const unit = ['days', 'months', 'years'].includes(raw.unit) ? raw.unit : 'days';
      return { mode: 'relative', amount, unit };
    }
  }
  return null;
}

/**
 * Resolve a (normalized) date-limit into an absolute ISO date string
 * that HenaketDatePicker can consume via its minDate / maxDate props.
 *
 * Returns undefined when there's no limit. The fixed-mode path also
 * returns undefined for empty strings so partial admin input (mode
 * picked but no date filled in yet) doesn't accidentally lock the
 * picker to "1970-01-01".
 */
export function resolveDateLimit(limit) {
  const norm = normalizeDateLimit(limit);
  if (!norm) return undefined;
  if (norm.mode === 'fixed') {
    return norm.value || undefined;
  }
  if (norm.mode === 'relative') {
    return addToTodayIso(norm.amount, norm.unit);
  }
  return undefined;
}

/**
 * Today + (amount, unit) → ISO date string (YYYY-MM-DD), local time.
 *
 * Why local time, not UTC: the picker operates on local-calendar
 * dates (Armenian users picking 2026-06-15 mean June 15 in their
 * local calendar, not a UTC midnight). All ISO strings the picker
 * uses are "naive" local dates with no timezone suffix.
 *
 * Edge cases:
 *   - Adding months/years that overflow the target month (e.g.
 *     Jan 31 + 1 month → Feb 31 doesn't exist) → JS Date clamps
 *     forward (Feb 31 → Mar 3). For platform purposes this is
 *     acceptable; the alternative ("Feb 28") is also defensible.
 *     If users complain, we can swap to the clamp-back variant.
 */
export function addToTodayIso(amount, unit) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (unit === 'days')   d.setDate(d.getDate() + amount);
  if (unit === 'months') d.setMonth(d.getMonth() + amount);
  if (unit === 'years')  d.setFullYear(d.getFullYear() + amount);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/* ============================================================
 * Date-element field normalization (0.35.97)
 *
 * The form-element shape for "date" gained several new fields
 * over time:
 *   non_working_days, working_overrides, non_working_overrides,
 *   non_working_color, disabled_ranges
 *
 * Older saved forms have only `holidays`. This helper makes both
 * shapes look the same to the render code:
 *
 *   - holidays merged into non_working_overrides (deduped)
 *   - missing fields filled with sensible defaults
 *
 * Returns a fresh object — never mutates the input — so callers
 * can use the result as-is in props without worrying about
 * accidental aliasing.
 * ============================================================ */
export function normalizeDateElement(element) {
  const e = element || {};

  /* 0.35.98 — Preserve EMPTY entries (empty strings, partial
   * ranges). The previous version filtered them, which broke the
   * configurator: clicking "+ Add" on DateListEditor added an
   * empty string row, normalize promptly removed it, the row
   * never appeared, the button looked broken. The right place
   * for filtering is the picker render — call dateElementForPicker
   * below before passing arrays to HenaketDatePicker.
   *
   * Legacy `holidays` is still merged into non_working_overrides
   * here (the one piece of legacy migration this function does),
   * deduped against any existing entries. Empty strings inside
   * either source array are preserved so mid-edit rows stay
   * visible in the configurator. */
  const merged = [];
  const seen = new Set();
  const pushEntry = (s) => {
    if (typeof s !== 'string') return;
    // Empty strings stay (one per add-row click), but we don't
    // dedupe them — multiple empty rows would be allowed too,
    // matching the UX of the disabled-range editor.
    if (s === '') { merged.push(''); return; }
    if (seen.has(s)) return;
    seen.add(s);
    merged.push(s);
  };
  if (Array.isArray(e.non_working_overrides)) {
    for (const iso of e.non_working_overrides) pushEntry(iso);
  }
  if (Array.isArray(e.holidays)) {
    for (const iso of e.holidays) pushEntry(iso);
  }

  return {
    label:                 e.label || '',
    name:                  getElementId(e),
    required:              !!e.required,
    default:               typeof e.default === 'string' ? e.default : '',
    placeholder:           typeof e.placeholder === 'string' ? e.placeholder : '',
    min_date:              e.min_date ?? null,
    max_date:              e.max_date ?? null,
    non_working_days:      Array.isArray(e.non_working_days) ? e.non_working_days : [0, 6],
    working_overrides:     Array.isArray(e.working_overrides) ? e.working_overrides : [],
    non_working_overrides: merged,
    non_working_color:     typeof e.non_working_color === 'string' ? e.non_working_color : '',
    disabled_ranges:       Array.isArray(e.disabled_ranges) ? e.disabled_ranges : [],
  };
}

/**
 * 0.35.98 — Picker-ready props derived from a normalized date
 * element. Filters out the mid-edit empties that normalize keeps
 * for the configurator's sake. Use this immediately before passing
 * to HenaketDatePicker on the canvas / live preview / runtime.
 */
export function dateElementForPicker(element) {
  const n = normalizeDateElement(element);
  return {
    ...n,
    working_overrides:     n.working_overrides.filter(s => s),
    non_working_overrides: n.non_working_overrides.filter(s => s),
    disabled_ranges:       n.disabled_ranges.filter(r => r?.start && r?.end),
  };
}
